import { Component, type ErrorInfo, type ReactNode } from 'react';

interface NavioFitErrorBoundaryProps {
  children: ReactNode;
}

interface NavioFitErrorBoundaryState {
  error: Error | null;
}

export class NavioFitErrorBoundary extends Component<
  NavioFitErrorBoundaryProps,
  NavioFitErrorBoundaryState
> {
  state: NavioFitErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): NavioFitErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (!import.meta.env.VITE_SENTRY_DSN) return;

    void import('@sentry/react')
      .then(({ captureException }) => {
        captureException(error, {
          contexts: { react: { componentStack: info.componentStack } },
        });
      })
      .catch((captureError: unknown) => {
        console.error('[observability] Failed to report render error:', captureError);
      });
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-lg font-semibold">Something went wrong</h1>
        <p className="text-sm text-muted-foreground">
          The error has been logged. Try again, or contact support if it persists.
        </p>
        <button
          onClick={this.reset}
          className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Try again
        </button>
      </div>
    );
  }
}
